import { Injectable } from '@angular/core';
import {Observable} from "rxjs";
import {AuthToken, ILoggedInPatient, INewAccount} from "../common/common.types";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {catchError, map} from "rxjs/operators";
import {AppConfigurationService} from "../../common/app-config/app-configuration.service";
import {AuthProviderService} from "./auth-provider.service";
import {LogManager} from "../../common/log/log.manager";
import {LogCat} from "../../common/log/log.categories";
import {PatientService} from "./patient.service";
import {PatientAccountPreferencesService} from "./patient.account.preferences.service";
import {IntlServicePP} from "../../common/intl/services/intl.service";

@Injectable({
  providedIn: 'root'
})
export class LoginInternalService {

  constructor(private httpClient: HttpClient,
              private configService: AppConfigurationService,
              private tokenProvider: AuthProviderService,
              private patientService: PatientService,
              private intlService: IntlServicePP,
              private patientAccountPreferencesService: PatientAccountPreferencesService) {
  }

  public async login(username: string, password: string): Promise<boolean> {
    const config =  this.configService.Config;

    const xmlRequestParam = encodeURIComponent('userName') + '=' +
      encodeURIComponent(username) + '&' +
      encodeURIComponent("password") + '=' + encodeURIComponent(password) + '&' +
      encodeURIComponent("grant_type") + '=' + encodeURIComponent("password");

    const reqSpec = {
      headers: new HttpHeaders(
        {
          ["Content-Type"]: 'application/x-www-form-urlencoded;charset=utf-8'
        })
    };

    const patientSubj = await this.httpClient.post<AuthToken>(config.AuthServerUrl + '/OAuth/PatientPortalInternalAuth', xmlRequestParam, reqSpec)
      .pipe(map((token: AuthToken) => {
        if (token && token.access_token) {
          return this.tokenProvider.setCredentials(token);
        }
        return  null;
      })).toPromise();

    if (patientSubj) {
      const identity = await this.patientService.getPatientDetails().toPromise();

      // If this is the first user login, currently selected system language will be set as user's default language
      if (identity.accountPreferences.defaultLanguageKey === 0 && this.intlService.usedLanguage) {
        this.patientAccountPreferencesService.setDefaultAccountLanguage(this.intlService.usedLanguage.languageKey);
      } else if (identity.accountPreferences.defaultLanguageKey !== this.intlService.usedLanguage.languageKey) {
        // If the system language was changed before login and it is different from user's default language - user language will be updated
        if (this.intlService.defaultBrowserLanguage && this.intlService.usedLanguage &&
          this.intlService.defaultBrowserLanguage.languageKey !== this.intlService.usedLanguage.languageKey) {
          this.patientAccountPreferencesService.setDefaultAccountLanguage(this.intlService.usedLanguage.languageKey);
        } else {
          const userLang = this.intlService.cachedLanguages.find(l => l.languageKey === identity.accountPreferences.defaultLanguageKey);
          if (userLang) {
            this.intlService.setLanguage(userLang, true);
          }
        }
      }

      patientSubj.next(identity);

      LogManager.debug(LogCat.Login.ExternalLogin, `login succeeded for patient - ${identity}`);
      return true;
    } else {
      return false;
    }
  }
}
